import * as React from 'react'
import { Loader2, RefreshCw, Unplug } from 'lucide-react'
import {
  useAppSelector,
  useConnectMTConnectAgentMutation,
  useDeregisterMtcLiveReadingsMutation
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { ConfirmDialog } from '../ConfigurePage/components/DeleteConfirmDialog'

export interface MtConnectAgentActionsProps {
  agentUrl: string
  computeUrn: string
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>
    if (record.data && typeof record.data === 'object') {
      const data = record.data as Record<string, unknown>
      if (typeof data.error === 'string') return data.error
      if (typeof data.message === 'string') return data.message
    }
    if (typeof record.error === 'string') return record.error
  }
  return String(error)
}

/**
 * Acquisition controls for a graph `TagProvider` backed by an MTConnect Agent.
 * Deregistration stops live collection and removes proxy registry rows only.
 */
export function MtConnectAgentActions({
  agentUrl,
  computeUrn
}: MtConnectAgentActionsProps): React.ReactElement {
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [rescanOpen, setRescanOpen] = React.useState(false)
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [status, setStatus] = React.useState<'idle' | 'success' | 'error'>('idle')
  const [statusMessage, setStatusMessage] = React.useState('')

  const siteId = useAppSelector((state) => state.ingest.siteId)
  const [rescan, rescanState] = useConnectMTConnectAgentMutation()
  const [deregister, deregisterState] = useDeregisterMtcLiveReadingsMutation()

  const handleRescan = async () => {
    setStatus('idle')
    setStatusMessage('')
    try {
      const result = await rescan({
        agentUrl,
        siteId,
        computeUrn,
        reconcileDataItems: true,
        ...(username.trim() ? { username: username.trim() } : {}),
        ...(password ? { password } : {})
      }).unwrap()
      setStatus('success')
      setStatusMessage(
        `Re-scan started as ingest job ${result.jobId}. New and changed DataItems will be merged; removed DataItems will appear as deletions in the ingest branch diff.`
      )
      setRescanOpen(false)
      setPassword('')
    } catch (err) {
      setStatus('error')
      setStatusMessage(errorMessage(err))
    }
  }

  const handleConfirm = async () => {
    setStatus('idle')
    setStatusMessage('')
    try {
      const result = await deregister({ agentUrl })
      setStatus('success')
      setStatusMessage(
        `Stopped live collection for this Agent (${result.tagsDeregistered} tag${
          result.tagsDeregistered === 1 ? '' : 's'
        } deregistered). Ontology tags and historical readings are preserved.`
      )
      setConfirmOpen(false)
    } catch (err) {
      setStatus('error')
      setStatusMessage(errorMessage(err))
    }
  }

  return (
    <div className="flex flex-col gap-2 border-b border-line px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={rescanState.isLoading}
          onClick={() => setRescanOpen(true)}
        >
          {rescanState.isLoading ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 size-3.5" />
          )}
          Re-scan device model
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-st-down/30 text-st-down hover:bg-st-down-bg"
          disabled={deregisterState.isLoading}
          onClick={() => setConfirmOpen(true)}
        >
          {deregisterState.isLoading ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          ) : (
            <Unplug className="mr-1.5 size-3.5" />
          )}
          Deregister live readings
        </Button>
        <span className="truncate text-xs text-ink-3" title={agentUrl}>
          {agentUrl}
        </span>
      </div>

      {status === 'success' && (
        <p className="m-0 text-xs text-st-up" role="status">
          {statusMessage}
        </p>
      )}
      {status === 'error' && (
        <p className="m-0 text-xs text-st-down" role="alert">
          {statusMessage}
        </p>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Deregister live readings?"
        description="Stops live collection and removes the history registration. Ontology tags and historical readings are preserved."
        confirmLabel="Deregister"
        destructive
        busy={deregisterState.isLoading}
        onConfirm={() => {
          void handleConfirm()
        }}
      />
      <Dialog open={rescanOpen} onOpenChange={setRescanOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Re-scan MTConnect device model?</DialogTitle>
            <DialogDescription>
              Probes the Agent again and starts an ingest job on a reviewable branch. Existing
              DataItems are merged by tag path; removed DataItems are staged as deletions.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <p className="m-0 break-all font-mono text-xs text-ink-3">{agentUrl}</p>
            <div className="grid gap-1.5">
              <Label htmlFor="mtconnect-rescan-username" className="text-xs">
                Basic-auth username
              </Label>
              <Input
                id="mtconnect-rescan-username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mtconnect-rescan-password" className="text-xs">
                Basic-auth password
              </Label>
              <Input
                id="mtconnect-rescan-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={rescanState.isLoading}
              onClick={() => setRescanOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={rescanState.isLoading}
              onClick={() => void handleRescan()}
            >
              {rescanState.isLoading ? <Loader2 className="animate-spin" /> : null}
              Start re-scan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
